/**
 * Talking to the Chrome extension that does the morning pull.
 *
 * The page cannot reach an extension directly, and must not be able to reach
 * Metabase itself — that browser rule is what makes the whole arrangement safe.
 * So the page posts a message into its own window, the extension's content
 * script (`extension/bridge.js`) hears it, and exactly two message types are
 * relayed: "are you there?" and "run now". The page cannot supply an endpoint,
 * a secret or a payload.
 *
 * WHY EVERY ANSWER HAS A TIMEOUT
 *
 * If the extension is not installed there is no content script, so nothing
 * replies — not an error, not a refusal, nothing at all. Silence is the answer,
 * and a promise with no timeout would simply never settle and leave the button
 * saying "Checking…" forever.
 *
 * WHY "NO REPLY" IS NOT THE SAME AS "NOT INSTALLED"
 *
 * Chrome orphans the content script when an extension is reloaded or updated.
 * The script is still on the page and still answers, but `chrome.runtime` is
 * dead and it replies `{installed:false, error:'Extension context
 * invalidated.'}`. Reported as "not installed" that is a lie, and it sends
 * somebody off to reinstall an extension that is sitting right there and needs
 * a page refresh. Seen for real during testing.
 */

const PAGE = 'agentcare-page'
const EXT = 'agentcare-extension'

/** A ping should be instant. If it is not, nothing is listening. */
export const PING_MS = 2_000

/**
 * A real pull is one Metabase request plus a write, and the joiners card has
 * been measured at ~160 ms — but a cold service worker, a slow morning and a
 * sign-in detour all land inside this one call. Generous on purpose: a timeout
 * here reports a failure for a run that is still going and probably about to
 * succeed.
 */
export const SYNC_MS = 180_000

type Reply = Record<string, unknown>

let counter = 0
/**
 * Correlation id — unguessable where the platform allows it.
 *
 * A counter starting at 1 on every page load is guessable by anything else
 * running on the page, and the origin and source checks below only prove *same
 * window, same origin* — which is exactly what an in-page script is. A
 * compromised tag or a CDN widget could forge `{source:'agentcare-extension',
 * id:'agentcare-1'}` and either make the app render an Update button on a
 * machine with no extension, or put its own words into the app's chrome in
 * front of somebody with no way to tell them from ours.
 *
 * `crypto.randomUUID` is gated on a secure context, so the fallback below is
 * what a plain-`http` build would get — and it is guessable, which is stated
 * here rather than glossed. The app is served from `https://reneeroark.com`
 * only, so the fallback is a development path; if that ever stops being true,
 * this is the line that needs revisiting.
 */
const nextId = () => {
  const uuid = globalThis.crypto?.randomUUID?.()
  return uuid ? `agentcare-${uuid}` : `agentcare-${++counter}-${Date.now()}`
}

function ask(type: 'ping' | 'sync-now', timeoutMs: number): Promise<Reply | null> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') return resolve(null)
    const id = nextId()
    let done = false

    const finish = (value: Reply | null) => {
      if (done) return
      done = true
      window.removeEventListener('message', onMessage)
      clearTimeout(timer)
      resolve(value)
    }

    const onMessage = (event: MessageEvent) => {
      // Our own window only. A reply that came from an iframe or another origin
      // is not from the bridge, whatever it claims about itself.
      if (event.source !== window) return
      if (event.origin !== window.location.origin) return
      const data = event.data as Reply | null
      if (!data || data.source !== EXT) return
      if (data.id !== id) return
      finish((data.payload as Reply) ?? {})
    }

    const timer = setTimeout(() => finish(null), timeoutMs)
    window.addEventListener('message', onMessage)
    window.postMessage({ source: PAGE, type, id }, window.location.origin)
  })
}

/** The extension's own record of the last run it did. */
export type LastRun = {
  ok?: boolean
  trigger?: string
  reason?: string
  message?: string
  at?: string
  created?: number
  enrolled?: number
  alreadyDone?: boolean
}

export type ExtensionState =
  | { state: 'ready'; last: LastRun | null; succeededToday: boolean; signingIn: boolean }
  /** No content script answered. The extension is not installed here. */
  | { state: 'missing' }
  /** It answered, but its own context is dead. The page needs reloading. */
  | { state: 'stale' }
  /** It answered with something else going wrong. */
  | { state: 'broken'; message: string }

/**
 * The message Chrome gives an orphaned content script.
 *
 * Matched loosely on purpose — the exact sentence is Chrome's and has changed
 * between versions, and being wrong here means telling her to reinstall a
 * working extension.
 */
const isInvalidated = (message: unknown): boolean =>
  typeof message === 'string' && /context invalidated|extension context/i.test(message)

/**
 * Chrome's own sentences, translated.
 *
 * These reach the screen otherwise. "The message port closed before a response
 * was received" is a true statement about a service worker and a meaningless
 * one to the person reading it.
 */
function humanise(message: string): string {
  if (/message port closed|receiving end does not exist/i.test(message)) {
    return 'The update stopped before it finished. Press Update again — this usually works the second time.'
  }
  return message
}

/**
 * The bridge's own failure, if it had one.
 *
 * Separate from `readState` because the two payloads are different shapes and
 * conflating them was a real defect. A `ping` answer always carries
 * `installed`; a `sync-now` answer is the background script's run result
 * relayed verbatim and carries no such field — so a strict `installed === true`
 * test, correct for the ping, declared every successful run to be a missing
 * extension.
 *
 * `bridge.js` only ever adds `installed:false` itself, and only when
 * `chrome.runtime` refused it. That is the one thing worth reading here.
 */
function bridgeFailure(payload: Reply): ExtensionState | null {
  if (payload.installed !== false) return null
  if (isInvalidated(payload.error)) return { state: 'stale' }
  return payload.error
    ? { state: 'broken', message: humanise(String(payload.error)) }
    : { state: 'missing' }
}

function readState(payload: Reply | null): ExtensionState {
  if (!payload) return { state: 'missing' }
  /**
   * `installed !== true`, not `installed === false`.
   *
   * The extension replies `{installed:true, error:...}` when its own storage
   * read fails, and an earlier version of this fell through to `ready` on
   * anything that was not literally `false` — so a broken extension rendered a
   * working button and said nothing was wrong. An empty `{}` did the same.
   */
  if (payload.installed !== true) {
    if (isInvalidated(payload.error)) return { state: 'stale' }
    return payload.error
      ? { state: 'broken', message: humanise(String(payload.error)) }
      : { state: 'missing' }
  }
  if (payload.error) {
    if (isInvalidated(payload.error)) return { state: 'stale' }
    return { state: 'broken', message: humanise(String(payload.error)) }
  }
  return {
    state: 'ready',
    last: (payload.last as LastRun | null) ?? null,
    succeededToday: payload.succeededToday === true,
    signingIn: payload.signingIn === true,
  }
}

export async function pingExtension(timeoutMs: number = PING_MS): Promise<ExtensionState> {
  return readState(await ask('ping', timeoutMs))
}

export type SyncNow =
  /** It ran and finished. */
  | { state: 'done'; created: number; enrolled: number }
  /** Somebody else had today covered. Nothing to do, and that is a success. */
  | { state: 'already-done' }
  /** A run is in flight right now — very often this machine's own alarm. */
  | { state: 'in-progress' }
  /** Metabase has been opened in a tab; it finishes by itself once she signs in. */
  | { state: 'signing-in'; message: string }
  /** It ran and refused, and said why in words worth showing. */
  | { state: 'refused'; message: string }
  /**
   * No answer inside the window. NOT the same as missing: the ping proved the
   * extension is there, so silence here means the run is slow, not absent.
   */
  | { state: 'timeout' }
  | { state: 'missing' }
  | { state: 'stale' }

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0)

export async function runSyncNow(timeoutMs: number = SYNC_MS): Promise<SyncNow> {
  const payload = await ask('sync-now', timeoutMs)
  /**
   * Silence means something different here than it does for a ping.
   *
   * A ping that goes unanswered in two seconds means nothing is listening. A
   * pull that goes unanswered in three minutes means the pull is slow — the
   * extension answered the ping a moment ago. Reporting it as "this laptop is
   * not set up" is the same lie as the invalidated-context one, and it takes
   * the button away with it.
   */
  if (!payload) return { state: 'timeout' }
  const failed = bridgeFailure(payload)
  if (failed) {
    if (failed.state === 'missing') return { state: 'missing' }
    if (failed.state === 'stale') return { state: 'stale' }
    if (failed.state === 'broken') return { state: 'refused', message: failed.message }
  }

  const p = payload as Reply
  if (p.ok === true) {
    /**
     * Three different things arrive as `ok: true`, and two of them are not
     * "it updated". `alreadyDone` is the day already having a successful run;
     * `inProgress` is a run happening *right now* on some machine, answered
     * with HTTP 200 and no counts. Both used to come out as "Updated. No new
     * agents today." — which is false, and in the `inProgress` case sat beside
     * a strip still saying the roster had not updated since Friday.
     */
    if (p.alreadyDone === true) return { state: 'already-done' }
    if (p.inProgress === true) return { state: 'in-progress' }
    if (p.dryRun === true) {
      return {
        state: 'refused',
        // Same correction as `syncstatus.ts`: practice mode is `SYNC_DRY_RUN`
        // in Cloudflare, and there is no such extension option to turn off.
        message: 'That was a practice run, so nothing was saved. Practice mode is SYNC_DRY_RUN in Cloudflare, not a setting in this extension.',
      }
    }
    return { state: 'done', created: num(p.created), enrolled: num(p.enrolled) }
  }
  if (p.reason === 'signing-in') {
    return {
      state: 'signing-in',
      message:
        typeof p.message === 'string' && p.message
          ? p.message
          : 'Opening Metabase — sign in there and this will finish by itself.',
    }
  }
  return {
    state: 'refused',
    message:
      typeof p.message === 'string' && p.message
        ? p.message
        : 'The update did not run, and no reason was given.',
  }
}

/**
 * What to say about each state, in one sentence each.
 *
 * Kept beside the states rather than in the component so the wording is
 * testable — every one of these is a sentence a non-technical person acts on,
 * and "extension not installed" was wrong for a whole state until testing
 * caught it.
 */
export function explainState(state: ExtensionState): string | null {
  switch (state.state) {
    case 'ready':
      return null
    case 'missing':
      return 'This laptop is not set up to run the update. It has to be done from a computer with the Agent Care extension installed.'
    case 'stale':
      return 'The extension was updated since this page was opened. Refresh the page and the button will work again.'
    case 'broken':
      return state.message
  }
}
